import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
} from "@mui/material";
import { FormatQuote } from "@mui/icons-material";
import AnimatedSection from "../AnimatedSection";
import { gradients } from "../../../utils/theme";

/**
 * Testimonials Section Component
 *
 * Displays mall partner testimonials with ratings and company information.
 * Features mobile carousel with touch-and-hold pause functionality.
 */
const TestimonialsSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState<boolean>(false);
  const [touchStartTime, setTouchStartTime] = useState<number>(0);
  const testimonials = [
    {
      name: "Vikram Reddy",
      role: "Operations Manager",
      company: "Skyline Plaza, Mumbai",
      rating: 5,
      text: "70% faster entry during peak hours. Our customers love the seamless premium experience!",
      delay: 0,
    },
    {
      name: "Neha Kapoor",
      role: "Facility Head",
      company: "Crown Mall, New Delhi",
      rating: 5,
      text: "WhatsApp integration is brilliant - zero app downloads, instant payments, incredibly easy management.",
      delay: 0.2,
    },
    {
      name: "Arjun Mehta",
      role: "Parking Manager",
      company: "Metro Junction, Bangalore",
      rating: 5,
      text: "ROI in 3 months, zero training needed, 45% increase in customer satisfaction. Best decision ever!",
      delay: 0.4,
    },
  ];

  // Auto-play carousel on mobile
  React.useEffect(() => {
    if (isAutoPlayPaused || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [currentSlide, isAutoPlayPaused, testimonials.length]);

  // Touch and hold handlers
  const handleTouchStart = () => {
    setTouchStartTime(Date.now());
    setIsAutoPlayPaused(true);
  };

  const handleTouchEnd = () => {
    const touchDuration = Date.now() - touchStartTime;
    if (touchDuration < 500) {
      setTimeout(() => setIsAutoPlayPaused(false), 500);
    } else {
      setTimeout(() => setIsAutoPlayPaused(false), 2000);
    }
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlayPaused(true);
    setTimeout(() => setIsAutoPlayPaused(false), 3000);
  };

  return (
    <Box id="testimonials" sx={{ py: { xs: 6, sm: 8, md: 10, lg: 12 } }}>
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            sx={{
              fontSize: {
                xs: "2rem",
                sm: "2.25rem",
                md: "2.5rem",
                lg: "2.75rem",
              },
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              background: gradients.navyLux,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Mall Success Stories
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 6,
              textAlign: "center",
              color: "text.secondary",
              fontWeight: 400,
            }}
          >
            Join 100+ premium malls transforming customer experiences
          </Typography>
        </AnimatedSection>

        {/* Mobile Carousel / Desktop Grid */}
        {/* Mobile Carousel View */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "relative",
            overflow: "hidden",
            pb: 4,
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <Box
            sx={{
              display: "flex",
              transition: "transform 0.5s ease-in-out",
              transform: `translateX(-${currentSlide * 100}%)`,
            }}
          >
            {testimonials.map((testimonial, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: "100%",
                  px: 1,
                }}
              >
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    transition: "all 0.3s ease",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <FormatQuote
                      sx={{
                        fontSize: 40,
                        color: "#FFD700",
                        opacity: 0.3,
                        position: "absolute",
                        top: 16,
                        right: 16,
                      }}
                    />
                    <Rating
                      value={testimonial.rating}
                      readOnly
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        fontStyle: "italic",
                        color: "text.secondary",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#1a237e",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {testimonial.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ fontSize: "0.95rem" }}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {testimonial.role}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#FFD700", fontSize: "0.75rem" }}
                          fontWeight={600}
                        >
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Carousel Indicators */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 2,
            }}
          >
            {testimonials.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleDotClick(index)}
                sx={{
                  width: currentSlide === index ? 24 : 8,
                  height: 8,
                  borderRadius: 1,
                  background:
                    currentSlide === index
                      ? gradients.gold
                      : "rgba(26, 35, 126, 0.2)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Desktop Grid View */}
        <Grid
          container
          spacing={4}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <AnimatedSection delay={testimonial.delay}>
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    transition: "all 0.3s ease",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 10px 40px rgba(255,215,0,0.15)",
                      borderColor: "#FFD700",
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <FormatQuote
                      sx={{
                        fontSize: 48,
                        color: "#FFD700",
                        opacity: 0.3,
                        position: "absolute",
                        top: 16,
                        right: 16,
                      }}
                    />
                    <Rating
                      value={testimonial.rating}
                      readOnly
                      sx={{ mb: 2 }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "#1a237e",
                          width: 56,
                          height: 56,
                        }}
                      >
                        {testimonial.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#FFD700" }}
                          fontWeight={600}
                        >
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
